import { resolveUnitId } from '#/modules/imsMgmt/helper/materialUnit/resolveUnitId';
import { ROUNDING_MODE, resolveRoundingMode } from '#/utils/roundingMode';

function roundMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100;
}

function toNumberOrNull(value) {
  if (value === '' || value == null) {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/** 舊值 TAXABLE → TAX_EXCLUDED（request 枚舉）。 */
function normalizeTaxType(taxType) {
  if (taxType === 'TAXABLE') {
    return 'TAX_EXCLUDED';
  }
  return taxType || 'TAX_EXCLUDED';
}

/** 訂購量（表單 `orderQty`）。 */
export function sumPurchaseOrderLineQty(line) {
  return Number(line?.orderQty) || 0;
}

/**
 * 依明細＋稅別／稅率計算金額（**僅 UI 預覽**；write 不送派生金額）。
 * 對齊 v1 `calcTaxAmounts`：稅額 `Math.round`；TAX_INCLUDED 反推稅前。
 * taxRate 為百分比（例 5＝5%）；v1 傳 fraction（0.05）。
 * taxType：TAX_EXCLUDED／TAX_INCLUDED／TAX_EXEMPT。
 */
export function computePurchaseOrderAmount(lines = [], taxType, taxRate) {
  const subtotal = (lines ?? []).reduce((sum, line) => {
    const qty = sumPurchaseOrderLineQty(line);
    const price = Number(line.unitPrice) || 0;
    return sum + (qty * price);
  }, 0);

  const ratePercent = Number(taxRate) || 0;
  const rateFraction = ratePercent / 100;
  const resolvedType = normalizeTaxType(taxType);

  if (resolvedType === 'TAX_EXEMPT') {
    const subtotalAmount = roundMoney(subtotal);
    return {
      taxType: resolvedType,
      taxRate: ratePercent,
      subtotalAmount,
      taxAmount: 0,
      totalAmount: subtotalAmount,
    };
  }

  if (resolvedType === 'TAX_INCLUDED') {
    const totalAmount = roundMoney(subtotal);
    const subtotalAmount = rateFraction > 0
      ? roundMoney(totalAmount / (1 + rateFraction))
      : totalAmount;
    return {
      taxType: resolvedType,
      taxRate: ratePercent,
      subtotalAmount,
      taxAmount: Math.round(totalAmount - subtotalAmount),
      totalAmount,
    };
  }

  const subtotalAmount = roundMoney(subtotal);
  const taxAmount = Math.round(subtotalAmount * rateFraction);
  return {
    taxType: resolvedType,
    taxRate: ratePercent,
    subtotalAmount,
    taxAmount,
    totalAmount: roundMoney(subtotalAmount + taxAmount),
  };
}

export function createEmptyPurchaseOrderLine(overrides = {}) {
  return {
    mtrId: '',
    mtrNo: '',
    mtrName: '',
    unitId: '',
    unitOptions: null,
    orderQty: 1,
    unitPrice: '',
    remark: '',
    ...overrides,
  };
}

/** MtrPickerDialog 列 → 採購明細列。 */
export function mapMtrPickerRowToPurchaseOrderLine(row) {
  const mtrId = row?.optionMtrId ?? row?.id ?? '';
  if (!mtrId) {
    return null;
  }

  return createEmptyPurchaseOrderLine({
    mtrId,
    mtrNo: row.mtrNo ?? '',
    mtrName: row.name ?? '',
    unitPrice: row.baseUnitPrice ?? row.unitPrice ?? '',
  });
}

/**
 * 選料確認 → 明細全量同步：既有 mtrId 保留整列（含使用者改過的 unitPrice）；
 * 新 mtrId 才 map 新列並帶入基礎單價。
 */
export function mergePurchaseOrderLinesFromPickerSelection(
  existingLines = [],
  selectedRows = [],
  mapRow = mapMtrPickerRowToPurchaseOrderLine,
) {
  const existingById = new Map(
    (existingLines || [])
      .filter((line) => line.mtrId)
      .map((line) => [line.mtrId, line]),
  );

  return (selectedRows || [])
    .map((row) => {
      const id = row?.optionMtrId ?? row?.id;
      if (!id) {
        return null;
      }
      const kept = existingById.get(id);
      if (kept) {
        return kept;
      }
      return mapRow(row);
    })
    .filter(Boolean);
}

/**
 * 表單值 → create／update body（camelCase；API 層再 keysToSnake）。
 * Header：`receiptWarehouseId`、`roundingMode`、`taxType`；**不送**派生金額／
 * `exchangeRate`／`taxRate`／舊 `defaultWarehouseId`。
 * Line：`unitId`、`qtySummary.orderQty`；**不送** `packages`／`unit_conversion`／
 * 扁平 `orderQty`／列金額。
 */
export function buildPurchaseOrderWritePayload(values, { updatedAt = null } = {}) {
  const taxType = normalizeTaxType(values.taxType);

  const lines = (values.lines ?? []).map((line) => {
    const row = {
      mtrId: line.mtrId,
      qtySummary: {
        orderQty: sumPurchaseOrderLineQty(line),
      },
      unitPrice: toNumberOrNull(line.unitPrice),
      remark: line.remark || null,
    };

    if (line.unitId) {
      row.unitId = line.unitId;
    }

    return row;
  });

  const payload = {
    purchaseOrderDate: values.purchaseOrderDate || null,
    supplierId: values.supplierId || null,
    currencyId: values.currencyId || null,
    paymentTermId: values.paymentTermId || null,
    receiptWarehouseId: values.receiptWarehouseId || null,
    taxType,
    roundingMode: resolveRoundingMode(values.roundingMode),
    issueReason: values.issueReason || null,
    remark: values.remark || null,
    lines,
  };

  if (updatedAt) {
    payload.updatedAt = updatedAt;
  }

  return payload;
}

function resolvePurchaseOrderLineUnitId(line) {
  return resolveUnitId(line?.unit)
    || String(line?.unitId ?? '').trim()
    || '';
}

function resolvePurchaseOrderLineOrderQty(line) {
  const fromSummary = line?.qtySummary?.orderQty;
  if (fromSummary !== '' && fromSummary != null) {
    return fromSummary;
  }
  return line?.orderQty ?? '';
}

/** BE `amount_summary.tax_rate` 為 fraction（0.05）；表單預覽用百分比。 */
function taxRateToFormPercent(taxRate) {
  if (taxRate == null || taxRate === '') {
    return 5;
  }
  const n = Number(taxRate);
  if (!Number.isFinite(n)) {
    return 5;
  }
  if (n > 0 && n <= 1) {
    return Math.round(n * 10000) / 100;
  }
  return n;
}

/** detail → 表單 defaultValues。Header 金額／稅別只讀 `amountSummary`。 */
export function mapPurchaseOrderDetailToFormValues(detail) {
  const amountSummary = detail?.amountSummary;
  return {
    purchaseOrderDate: detail?.purchaseOrderDate ?? '',
    supplierId: detail?.supplier?.id ?? detail?.supplierId ?? '',
    currencyId: detail?.currencyId ?? detail?.currency?.id ?? '',
    paymentTermId: detail?.paymentTerm?.id ?? detail?.paymentTermId ?? '',
    receiptWarehouseId: detail?.receiptWarehouse?.id
      ?? detail?.receiptWarehouseId
      ?? detail?.defaultWarehouse?.id
      ?? detail?.defaultWarehouseId
      ?? '',
    exchangeRate: detail?.exchangeRate ?? '',
    taxType: normalizeTaxType(amountSummary?.taxType ?? detail?.taxType),
    taxRate: taxRateToFormPercent(amountSummary?.taxRate),
    roundingMode: resolveRoundingMode(
      detail?.roundingMode ?? amountSummary?.roundingMode,
      ROUNDING_MODE.HALF_UP,
    ),
    issueReason: detail?.issueReason ?? '',
    remark: detail?.remark ?? '',
    lines: (detail?.lines ?? []).map((line) => {
      const mtr = line.mtr ?? null;
      return {
        mtrId: mtr?.id ?? line.mtrId ?? '',
        mtrNo: mtr?.mtrNo ?? '',
        mtrName: mtr?.name ?? '',
        unitId: resolvePurchaseOrderLineUnitId(line),
        unitOptions: mtr?.unitOptions
          ?? line?.unitOptions
          ?? null,
        orderQty: resolvePurchaseOrderLineOrderQty(line),
        unitPrice: line.unitPrice ?? '',
        remark: line.remark ?? '',
      };
    }),
  };
}

/** detail 巢狀 lookup → select option（缺 HTTP 時 edit 併入）。 */
export function mapLookupToSelectOption(lookup) {
  if (!lookup?.id) {
    return null;
  }
  const label = [lookup.code, lookup.name].filter(Boolean).join(' - ') || lookup.id;
  return { value: lookup.id, label };
}

export function mergeLookupOption(options = [], lookup) {
  const option = mapLookupToSelectOption(lookup);
  if (!option) {
    return options;
  }
  if (options.some((item) => item.value === option.value)) {
    return options;
  }
  return [...options, option];
}
